import { ClaimStatusEnum, PolicyStatus } from 'sl-sdk';
import type { InsuranceClaimType, PolicyType } from 'sl-sdk';

const POLICY_RANK: Record<string, number> = {
  [PolicyStatus.ACTIVE]: 0,
  [PolicyStatus.ENDED]: 1,
  [PolicyStatus.TERMINATED]: 2,
};

const TERMINAL_CLAIM_STATUSES = new Set<ClaimStatusEnum>([
  ClaimStatusEnum.PAID,
  ClaimStatusEnum.REJECTED,
  ClaimStatusEnum.ABANDONED,
]);

export function sortPolicies(policies: PolicyType[]): PolicyType[] {
  return [...policies].sort((a, b) => {
    const rankA = POLICY_RANK[a.policyStatus] ?? 50;
    const rankB = POLICY_RANK[b.policyStatus] ?? 50;
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return b.id - a.id;
  });
}

function claimRank(status: ClaimStatusEnum): number {
  if (status === ClaimStatusEnum.OPEN) {
    return 0;
  }
  if (TERMINAL_CLAIM_STATUSES.has(status)) {
    return 2;
  }
  return 1;
}

export function sortClaims(claims: InsuranceClaimType[]): InsuranceClaimType[] {
  return [...claims].sort((a, b) => {
    const rankA = claimRank(a.claimCoverageStatus);
    const rankB = claimRank(b.claimCoverageStatus);
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return b.id - a.id;
  });
}

export function formatAppVersion(device: { appVersionIos: number | null; appVersionAndroid: number | null }): string {
  if (device.appVersionIos != null) {
    return `iOS ${device.appVersionIos}`;
  }
  if (device.appVersionAndroid != null) {
    return `Android ${device.appVersionAndroid}`;
  }
  return '—';
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

// Sharelock stores phones in E.164 (+33612345678). Zendesk usually does too, but agents also
// type national numbers, so a French national number is offered as a second candidate.
export function phoneLookupCandidates(rawPhone: string): string[] {
  const compact = rawPhone.replace(/[\s.\-()]/g, '');
  const international = compact.startsWith('00') ? `+${compact.slice(2)}` : compact;
  const candidates: string[] = [];
  if (international.startsWith('+')) {
    // "+33 (0)6…" keeps the trunk zero once compacted, while Sharelock stores "+336…".
    candidates.push(international.startsWith('+330') ? `+33${international.slice(4)}` : international);
  } else if (/^0\d{9}$/.test(compact)) {
    candidates.push(`+33${compact.slice(1)}`, compact);
  } else if (compact.length > 0) {
    candidates.push(compact);
  }
  return Array.from(new Set(candidates));
}
